import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { exportToCSV as _exportToCSV } from './exportToCSV';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

/**
 * Export Utilities for Admin Reports
 * Handles Excel generation with various formatting options
 */

/**
 * Basic Excel export without styling
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {Object} options - Export options
 * @param {string} options.sheetName - Sheet name (default: 'Sheet1')
 * @param {Array} options.columnOrder - Array of column keys in desired order
 * @param {Object} options.columnHeaders - Map of key to display name
 */
export async function exportToExcel(data, filename, options = {}) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    try {
        const {
            sheetName = 'Sheet1',
            columnOrder = null,
            columnHeaders = null
        } = options;

        // Transform data if column order or headers specified
        let transformedData = data;
        if (columnOrder || columnHeaders) {
            transformedData = data.map(row => {
                const newRow = {};
                const keys = columnOrder || Object.keys(row);
                keys.forEach(key => {
                    const header = columnHeaders?.[key] || key;
                    newRow[header] = row[key];
                });
                return newRow;
            });
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(sheetName);

        if (transformedData.length > 0) {
            sheet.columns = Object.keys(transformedData[0]).map(key => ({ header: key, key }));
            sheet.addRows(transformedData);
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const timestamp = moment(new Date()).format('yyyyMMDDHHmmss');
        saveAs(new Blob([buffer], { type: XLSX_MIME }), `${filename}_${timestamp}.xlsx`);
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        throw error;
    }
}

/**
 * Export with styled headers and alternating row colors
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {Object} styling - Styling options
 * @param {string} styling.headerColor - Header background color (hex without #)
 * @param {string} styling.oddRowColor - Odd row background color (hex without #)
 * @param {string} styling.evenRowColor - Even row background color (hex without #)
 * @param {string} styling.sheetName - Sheet name
 */
export async function exportToStyledExcel(data, filename, styling = {}) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    try {
        const {
            headerColor = '4F81BD',
            oddRowColor = 'DBE5F1',
            evenRowColor = 'B8CCE4',
            sheetName = 'Report'
        } = styling;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(sheetName);

        if (data.length > 0) {
            sheet.columns = Object.keys(data[0]).map(key => ({ header: key, key }));

            // Style header row
            sheet.getRow(1).eachCell(cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + headerColor } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });

            // Add data rows with alternating colors
            data.forEach((row, i) => {
                const excelRow = sheet.addRow(row);
                const rowColor = i % 2 === 0 ? oddRowColor : evenRowColor;
                excelRow.eachCell(cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + rowColor } };
                });
            });
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const timestamp = moment(new Date()).format('yyyyMMDDHHmmss');
        saveAs(new Blob([buffer], { type: XLSX_MIME }), `${filename}_${timestamp}.xlsx`);
    } catch (error) {
        console.error('Error exporting styled Excel:', error);
        throw error;
    }
}

/**
 * Export multiple sheets to one workbook
 * @param {Array} sheets - Array of sheet objects
 * @param {string} sheets[].name - Sheet name
 * @param {Array} sheets[].data - Sheet data
 * @param {string} filename - Workbook filename
 */
export async function exportMultiSheet(sheets, filename) {
    if (!sheets || sheets.length === 0) {
        console.warn('No sheets to export');
        return;
    }

    try {
        const workbook = new ExcelJS.Workbook();

        sheets.forEach(sheet => {
            if (sheet.data && sheet.data.length > 0) {
                const ws = workbook.addWorksheet(sheet.name || 'Sheet');
                ws.columns = Object.keys(sheet.data[0]).map(key => ({ header: key, key }));
                ws.addRows(sheet.data);
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const timestamp = moment(new Date()).format('yyyyMMDDHHmmss');
        saveAs(new Blob([buffer], { type: XLSX_MIME }), `${filename}_${timestamp}.xlsx`);
    } catch (error) {
        console.error('Error exporting multi-sheet Excel:', error);
        throw error;
    }
}

/**
 * Export to CSV — delegates to the pure native CSV implementation.
 * Re-exported here so existing callers (DownloadReportDialog etc.) need no import change.
 * @param {Array} data - Array of objects
 * @param {string} filename - Filename without extension
 */
export function exportToCSV(data, filename) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }
    // Derive column definitions from object keys
    const columns = Object.keys(data[0]).map(key => ({ key, label: key }));
    _exportToCSV(data, filename, columns);
}

/**
 * Format raw API data for Excel export
 * @param {Array} rawData - Raw API response data
 * @param {Object} columnMap - Map of API keys to Excel column names
 * @param {Function} transformRow - Optional row transformation function
 */
export function formatExcelData(rawData, columnMap, transformRow = null) {
    if (!rawData || rawData.length === 0) return [];

    return rawData.map(row => {
        let transformedRow = transformRow ? transformRow(row) : row;

        const formattedRow = {};
        Object.entries(columnMap).forEach(([apiKey, excelHeader]) => {
            formattedRow[excelHeader] = transformedRow[apiKey] ?? '';
        });

        return formattedRow;
    });
}

export default exportToExcel;
